import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

export interface SegmentProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  disabled?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
}

const Segment = React.forwardRef<HTMLDivElement, SegmentProps>(
  (
    {
      className,
      asChild = false,
      disabled = false,
      value,
      onValueChange,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        data-slot="segment"
        className={cn("wl-btn-segment", disabled && "wl-disabled", className)}
        {...props}
      />
    );
  }
);

export interface SegmentItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  selected?: boolean;
  value: string;
}

const SegmentItem = React.forwardRef<HTMLButtonElement, SegmentItemProps>(
  (
    { className, asChild = false, selected = false, value, children, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        data-slot="segment-item"
        type="button"
        className={cn("wl-btn", className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

export interface SegmentRadioProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  asChild?: boolean;
}

const SegmentRadio = React.forwardRef<HTMLInputElement, SegmentRadioProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "input";

    return (
      <Comp
        ref={ref}
        data-slot="segment-radio"
        type="radio"
        className={cn("wl-input-radio", className)}
        {...props}
      />
    );
  }
);

// Hook for managing segment state
export const useSegment = (defaultValue?: string) => {
  const [value, setValue] = React.useState(defaultValue || "");

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
  };

  return {
    value,
    onValueChange: handleValueChange,
  };
};

Segment.displayName = "Segment";
SegmentItem.displayName = "SegmentItem";
SegmentRadio.displayName = "SegmentRadio";

export { Segment, SegmentItem, SegmentRadio };
